<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { type CascaderOption } from 'naive-ui';
import {
  IconChatActive,
  IconClose,
  IconPlay,
  IconSearch,
  IconTime,
  IconUndo,
} from '@/components/design/icons';
import { ClauseValidationOptionType, type PlaybookRiskLevel } from '@/api';
import { usePlaybookRunWebSocket } from '@/composables/usePlaybookRunWebSocket';
import PreviewSegments from '@/office/components/playbooks/PreviewSegments.vue';
import ResultCard from '@/office/components/playbooks/ResultCard.vue';
import ResultPane from '@/office/components/playbooks/ResultPane.vue';
import SuggestionActions from '@/office/components/playbooks/SuggestionActions.vue';
import type { PlaybookClauseEntry } from '@/office/playbooks/mapPlaybookRunResults';
import {
  canFocusSuggestion,
  isActionableSuggestion,
  suggestionPreviewSegments,
  type PreviewSegment,
} from '@/office/playbooks/suggestionPreview';
import {
  findingAsSuggestion,
  useOfficePlaybookRunStore,
} from '@/office/stores/officePlaybookRunStore';
import { isOfficeAddinHost } from '@/office/utils';
import { addSuggestionCommentToWord } from '@/office/word/playbookWord';
import {
  clauseOptionTypeShortLabel,
  findingSeverityBucket,
  severityBucketRank,
  SEVERITY_ORDER,
  type SeverityBucket,
} from '@/services/playbooks/playbookRunDisplay';
import type { UncommonsCheckFinding, WordSuggestion } from '@/types/playbookWordAddin';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCollapse from '@/components/design/base/GaiaCollapse.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaRiskPill, { type GaiaRiskLevel } from '@/components/design/base/GaiaRiskPill.vue';
import GaiaStatusChip, { type GaiaChipTone } from '@/components/design/base/GaiaStatusChip.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import { playbookRiskLevelMap, playbookRunStatusMap } from '@/components/design/statusMaps';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * The Playbooks task pane: pick a playbook, run it against the open document,
 * and work through what came back — extractions, suggestions with redlines,
 * clause checks and uncommon passages — applying, commenting or declining
 * each in Word. The result vocabulary (risk pills, status chips, run status)
 * is the web run detail's, through the same status maps.
 */

const message = useGaiaToast();
const playbookStore = useOfficePlaybookRunStore();
const isInAddinHost = isOfficeAddinHost();

type ResultTab = 'extractions' | 'evaluations' | 'clause-validation' | 'uncommons';

const applyingAllOf = ref<'evaluations' | 'clauses' | null>(null);
const commentingSuggestionId = ref<string | null>(null);
const commentingFindingId = ref<string | null>(null);
const activeTab = ref<ResultTab>('evaluations');

function isResultTab(key: string): key is ResultTab {
  return (
    key === 'extractions' ||
    key === 'evaluations' ||
    key === 'clause-validation' ||
    key === 'uncommons'
  );
}

function selectTab(key: string): void {
  if (isResultTab(key)) activeTab.value = key;
}

/** Playbook nodes are prefixed so a branch value can never collide with a run uid. */
const PLAYBOOK_NODE_PREFIX = 'playbook:';
type RunCascaderOption = CascaderOption & { playbookUid?: string };
const runCascaderOptions = ref<CascaderOption[]>([]);
// The picker holds what the user last chose while the pane is mounted; only its
// menu is visible, opened by the history button. Which run is loaded is reported by
// the run-status row instead.
const selectedRunValue = ref<string | null>(null);
const showRunPicker = ref(false);

const isRunAllRunning = computed(() => playbookStore.isStartingRun || playbookStore.isRunActive);
const runAllDisabledReason = computed(() => {
  if (!isInAddinHost) return 'Open this feature inside the Word add-in.';
  if (!playbookStore.selectedPlaybookUid) return 'Select a playbook.';
  if (isRunAllRunning.value) return 'The playbook run is already in progress.';
  return '';
});
const canRunAll = computed(() => runAllDisabledReason.value === '');

/** The loaded run's status through the same adapter the web run list uses. */
const runStatus = computed(() =>
  playbookStore.run ? playbookRunStatusMap[playbookStore.run.status] : null
);
const runStartedLabel = computed(() =>
  playbookStore.run ? formatDateTimeForTable(playbookStore.run.created_at) : ''
);

/** The result tabs, each carrying how many results it holds. */
const paneTabs = computed<GaiaTabBarItem[]>(() => [
  { key: 'extractions', label: 'Extractions', count: playbookStore.extractions.length },
  { key: 'evaluations', label: 'Evaluations', count: playbookStore.suggestions.length },
  { key: 'clause-validation', label: 'Clauses', count: playbookStore.clauseEntries.length },
  { key: 'uncommons', label: 'Uncommons', count: playbookStore.uncommonsFindings.length },
]);

const CLAUSE_GROUPS: {
  key: string;
  name: string;
  optionType: ClauseValidationOptionType | null;
}[] = [
  ...Object.values(ClauseValidationOptionType).map(optionType => ({
    key: optionType,
    name: clauseOptionTypeShortLabel(optionType),
    optionType,
  })),
  { key: 'unmatched', name: 'Not matched', optionType: null },
];

/** A suggestion plus everything the card needs, so the template computes nothing. */
type SuggestionCard = {
  suggestion: WordSuggestion;
  segments: PreviewSegment[];
};

type SuggestionGroup = {
  name: SeverityBucket;
  cards: SuggestionCard[];
};

type ClauseGroupEntry = PlaybookClauseEntry & {
  cards: SuggestionCard[];
};

type UncommonsGroup = {
  bucket: SeverityBucket;
  findings: (UncommonsCheckFinding & { number: number })[];
};

function toCard(suggestion: WordSuggestion): SuggestionCard {
  return { suggestion, segments: suggestionPreviewSegments(suggestion) };
}

const extractionCards = computed(() => playbookStore.extractions.map(toCard));

const groupedSuggestions = computed<SuggestionGroup[]>(() => {
  const groups = new Map<SeverityBucket, SuggestionCard[]>();
  for (const suggestion of playbookStore.suggestions) {
    const name = (suggestion.risk_assessment?.label ?? 'Other') as SeverityBucket;
    const cards = groups.get(name);
    if (cards) {
      cards.push(toCard(suggestion));
    } else {
      groups.set(name, [toCard(suggestion)]);
    }
  }

  return [...groups.entries()]
    .map(([name, cards]) => ({ name, cards }))
    .sort((a, b) => severityBucketRank(a.name) - severityBucketRank(b.name));
});

const groupedClauseEntries = computed(() =>
  CLAUSE_GROUPS.map(group => ({
    ...group,
    entries: playbookStore.clauseEntries
      .filter(entry => entry.optionType === group.optionType)
      .map<ClauseGroupEntry>(entry => ({ ...entry, cards: entry.suggestions.map(toCard) })),
  })).filter(group => group.entries.length > 0)
);

const groupedUncommonsFindings = computed<UncommonsGroup[]>(() => {
  // Numbered over the whole run so a finding keeps its number across the groups.
  const numbered = playbookStore.uncommonsFindings.map((finding, index) => ({
    ...finding,
    number: index + 1,
  }));

  return SEVERITY_ORDER.map(bucket => ({
    bucket,
    findings: numbered.filter(finding => findingSeverityBucket(finding.severity) === bucket),
  })).filter(group => group.findings.length > 0);
});

// ── Vocabulary adapters — the web run detail's risk pills and status chips ──

/** Whether a severity bucket is a risk at all — "Other" is drawn as a plain tag. */
function severityHasRisk(bucket: SeverityBucket): boolean {
  return bucket !== 'Other';
}

/** A severity bucket as a risk pill level. Only meaningful where `severityHasRisk`. */
function severityRiskLevel(bucket: SeverityBucket): GaiaRiskLevel {
  switch (bucket) {
    case 'High':
      return 'high';
    case 'Medium':
      return 'medium';
    case 'Low':
      return 'low';
    default:
      return 'none';
  }
}

function clauseRiskLevel(level: PlaybookRiskLevel | null): GaiaRiskLevel {
  return level ? playbookRiskLevelMap[level].level : 'none';
}

/** What happened to a suggestion in Word, as a chip. */
function suggestionChip(suggestion: WordSuggestion): { tone: GaiaChipTone; label: string } {
  if (suggestion.status === 'accepted') return { tone: 'success', label: 'Applied' };
  if (suggestion.status === 'declined') return { tone: 'alert', label: 'Declined' };
  return { tone: 'neutral', label: 'Pending' };
}

usePlaybookRunWebSocket({
  runUids: () => (playbookStore.run ? [playbookStore.run.uid] : []),
  onRunUpdated: uid => void playbookStore.refreshRun(uid),
});

onMounted(async () => {
  await playbookStore.searchPlaybooks();
  // The pane unmounts on every bottom-tab switch — pick up whatever happened
  // to the run while the user was elsewhere.
  if (playbookStore.run) {
    await playbookStore.refreshRun(playbookStore.run.uid);
  }
});

async function refreshPlaybooks(): Promise<void> {
  await playbookStore.searchPlaybooks();
}

/** Compact enough for the task pane's narrow cascader column, e.g. "Aug 3, 17:04". */
function formatRunTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Playbooks are the cascader's first level; their runs are fetched on expand.
// Rebuilding drops the loaded children and with them the selection's label, so it
// keys off the set of playbooks rather than firing on every remote-search response.
watch(
  () => playbookStore.playbookOptions.map(option => String(option.value)).join(','),
  () => {
    selectedRunValue.value = null;
    runCascaderOptions.value = playbookStore.playbookOptions.map(option => ({
      label: String(option.label ?? option.value),
      value: `${PLAYBOOK_NODE_PREFIX}${String(option.value)}`,
      isLeaf: false,
    }));
  },
  { immediate: true }
);

async function loadRunOptions(option: CascaderOption): Promise<void> {
  const playbookUid = String(option.value).slice(PLAYBOOK_NODE_PREFIX.length);
  const result = await playbookStore.loadRuns(playbookUid);
  if (!result) {
    // Leave the branch unloaded so re-expanding retries; `runError` names the failure.
    return;
  }

  const runs = result.results;
  if (!runs.length) {
    option.children = [{ label: 'No runs yet', value: `empty:${playbookUid}`, disabled: true }];
    return;
  }

  const children: RunCascaderOption[] = runs.map(run => ({
    label: `${formatRunTimestamp(run.created_at)} · ${run.status}`,
    value: run.uid,
    playbookUid,
  }));

  const hidden = result.result_count - runs.length;
  if (hidden > 0) {
    children.push({
      label: `… ${hidden} older runs not shown`,
      value: `more:${playbookUid}`,
      disabled: true,
    });
  }

  option.children = children;
}

async function handleRunSelect(value: string | null, option: CascaderOption | null): Promise<void> {
  selectedRunValue.value = value;
  const playbookUid = option?.playbookUid;
  if (!value || typeof playbookUid !== 'string') {
    return;
  }

  await playbookStore.selectRun(playbookUid, value);
}

async function runPlaybook(): Promise<void> {
  if (!canRunAll.value) {
    return;
  }

  selectedRunValue.value = null;
  await playbookStore.startRun();
}

function resetRun(): void {
  selectedRunValue.value = null;
  playbookStore.reset();
}

async function applySuggestion(
  suggestion: WordSuggestion,
  { silent = false }: { silent?: boolean } = {}
): Promise<boolean> {
  try {
    const result = await playbookStore.applySuggestion(suggestion);
    if (!result) {
      return false;
    }

    if (!silent) {
      message.success(
        result.enabledChangeTracking
          ? 'Track Changes was enabled and the suggestion was applied.'
          : 'Suggestion applied to the Word document with Track Changes.'
      );
    }

    return true;
  } catch (error) {
    playbookStore.setSuggestionFailed(suggestion.id, true);
    if (!silent) {
      message.error(error instanceof Error ? error.message : 'Failed to apply suggestion.');
    }
    return false;
  }
}

async function focusSuggestion(suggestion: WordSuggestion): Promise<void> {
  try {
    await playbookStore.focusSuggestion(suggestion);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Could not find this suggestion in Word.'
    );
  }
}

async function addSuggestionComment(suggestion: WordSuggestion): Promise<void> {
  if (commentingSuggestionId.value) {
    return;
  }

  commentingSuggestionId.value = suggestion.id;
  try {
    await addSuggestionCommentToWord(suggestion);
    message.success('Suggestion added as a Word comment.');
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add suggestion comment.');
  } finally {
    commentingSuggestionId.value = null;
  }
}

async function applyAllPending(scope: 'evaluations' | 'clauses'): Promise<void> {
  const suggestions =
    scope === 'evaluations'
      ? [...playbookStore.pendingSuggestions]
      : [...playbookStore.pendingClauseSuggestions];
  if (applyingAllOf.value || !suggestions.length) {
    return;
  }

  const noun = scope === 'evaluations' ? 'suggestions' : 'clause suggestions';
  applyingAllOf.value = scope;
  try {
    let applied = 0;
    for (const suggestion of suggestions) {
      applied += (await applySuggestion(suggestion, { silent: true })) ? 1 : 0;
    }

    const failed = suggestions.length - applied;
    if (failed > 0) {
      message.warning(
        `Applied ${applied} of ${suggestions.length} ${noun}; ${failed} could not be applied.`
      );
    } else {
      message.success(`Applied ${applied} ${noun} to the Word document with Track Changes.`);
    }
  } finally {
    applyingAllOf.value = null;
  }
}

async function focusFinding(finding: UncommonsCheckFinding): Promise<void> {
  try {
    await playbookStore.focusFinding(finding);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Could not find this passage in Word.');
  }
}

/** A finding is anchored by its text alone, so that is all there is to look for. */
function canFocusFinding(finding: UncommonsCheckFinding): boolean {
  return Boolean(finding.text.trim());
}

async function addFindingComment(finding: UncommonsCheckFinding): Promise<void> {
  if (!canFocusFinding(finding) || commentingFindingId.value) {
    return;
  }

  commentingFindingId.value = finding.id;
  try {
    await addSuggestionCommentToWord(findingAsSuggestion(finding));
    message.success('Finding added as a Word comment.');
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add finding comment.');
  } finally {
    commentingFindingId.value = null;
  }
}
</script>

<template>
  <n-flex vertical :size="14" :wrap="false" :style="{ minWidth: 0 }">
    <n-flex vertical :size="4" :wrap="false">
      <gaia-header :level="1" size="dialog">Playbooks</gaia-header>
      <gaia-text size="meta" tone="subtle" :style="{ lineHeight: 1.5 }">
        Run a playbook against this document and apply what it suggests with Track Changes.
      </gaia-text>
    </n-flex>

    <gaia-banner v-if="!isInAddinHost" plain type="warning">
      Open this feature inside the Word add-in to read and update the current document.
    </gaia-banner>

    <gaia-banner v-if="playbookStore.playbooksError" plain type="error">
      Failed to load playbooks: {{ playbookStore.playbooksError }}
    </gaia-banner>

    <gaia-banner
      v-if="
        !playbookStore.playbooksLoading &&
        !playbookStore.playbookOptions.length &&
        !playbookStore.playbooksError
      "
      plain
      type="info"
    >
      No playbooks are available to this add-in session.
      <template #action>
        <gaia-link @click="refreshPlaybooks">Refresh</gaia-link>
      </template>
    </gaia-banner>

    <gaia-form-field label="Playbook">
      <n-flex align="center" :size="8" :wrap="false">
        <!-- Caller-owned options: the store's `playbookOptions` also feed the
             run cascader, so it keeps them. The field contributes the debounce
             and the prime-on-open. -->
        <gaia-remote-select
          :value="playbookStore.selectedPlaybookUid"
          :options="playbookStore.playbookOptions"
          :loading="playbookStore.playbooksLoading"
          clearable
          placeholder="Select a playbook"
          :debounce="400"
          :style="{ flex: 1, minWidth: 0 }"
          @update:value="playbookStore.selectPlaybook"
          @search="playbookStore.searchPlaybooks"
        />
        <!-- Only the cascader's menu is shown; it sits behind the history
             button so the menu anchors to it, and the button takes the clicks. -->
        <div class="run-picker">
          <gaia-tooltip>
            <template #trigger>
              <gaia-icon-button
                :dimension="34"
                aria-label="Load previous runs"
                @click="showRunPicker = true"
              >
                <n-icon :size="16" :component="IconTime" />
              </gaia-icon-button>
            </template>
            Load previous runs
          </gaia-tooltip>
          <n-cascader
            v-model:show="showRunPicker"
            :value="selectedRunValue"
            :options="runCascaderOptions"
            :on-load="loadRunOptions"
            :virtual-scroll="false"
            :theme-overrides="{ columnWidth: '180px' }"
            :menu-props="{ class: 'playbook-run-cascader-menu' }"
            check-strategy="child"
            remote
            class="run-picker__input"
            @update:value="handleRunSelect"
          />
        </div>
      </n-flex>
    </gaia-form-field>

    <gaia-tooltip :disabled="canRunAll">
      <template #trigger>
        <div>
          <gaia-button
            type="primary"
            block
            :disabled="!canRunAll"
            :loading="isRunAllRunning"
            @click="runPlaybook"
          >
            <template #icon>
              <n-icon :component="IconPlay" />
            </template>
            Run playbook
          </gaia-button>
        </div>
      </template>
      {{ runAllDisabledReason }}
    </gaia-tooltip>

    <n-flex v-if="playbookStore.run && runStatus" align="center" :size="8" :wrap="false">
      <gaia-text size="micro" tone="subtle">Loaded run</gaia-text>
      <gaia-status-indicator :status="runStatus.status" :glyph="runStatus.glyph">
        {{ runStatus.label }}
      </gaia-status-indicator>
      <gaia-text size="micro" tone="subtle">{{ runStartedLabel }}</gaia-text>
    </n-flex>

    <!-- One report for the whole run: a pane that produced partial results would
         otherwise never mention that the run itself failed. -->
    <gaia-banner v-if="playbookStore.runFailureMessage" plain type="error">
      {{ playbookStore.runFailureMessage }}
    </gaia-banner>

    <gaia-banner
      v-if="playbookStore.runError"
      plain
      type="error"
      :style="{ whiteSpace: 'pre-line' }"
    >
      {{ playbookStore.runError }}
    </gaia-banner>

    <gaia-tab-bar :items="paneTabs" :active="activeTab" @select="selectTab" />

    <!-- Every pane stays mounted so its fold state survives a tab switch. -->
    <div v-show="activeTab === 'extractions'">
      <result-pane
        :status="playbookStore.extractionStatus"
        :progress-label="playbookStore.extractionProgressLabel"
        :failures="playbookStore.extractionFailures"
        failures-title="Some extractions failed"
        idle-description="Run a playbook to see the values it extracts from this document."
        empty-description="No values were extracted from this document."
      >
        <n-flex vertical :size="10" :wrap="false">
          <result-card
            v-for="card in extractionCards"
            :key="card.suggestion.id"
            :title="card.suggestion.title"
            :focusable="canFocusSuggestion(card.suggestion)"
            @activate="focusSuggestion(card.suggestion)"
          >
            <template #header-extra>
              <suggestion-actions
                :suggestion="card.suggestion"
                :actionable="false"
                :focusing-id="playbookStore.focusingSuggestionId"
                :commenting-id="commentingSuggestionId"
                :applying-id="playbookStore.applyingSuggestionId"
                @focus="focusSuggestion"
              />
            </template>

            <preview-segments :segments="card.segments" />

            <template #reasoning>
              <p>{{ card.suggestion.explanation }}</p>
              <p v-if="card.suggestion.extraction_highlighted_text?.length">
                <strong>Highlighted text:</strong>
                {{ card.suggestion.extraction_highlighted_text.join(' | ') }}
              </p>
            </template>
          </result-card>
        </n-flex>
      </result-pane>
    </div>

    <div v-show="activeTab === 'evaluations'">
      <result-pane
        :status="playbookStore.classificationStatus"
        :progress-label="playbookStore.classificationProgressLabel"
        :failures="playbookStore.classificationFailures"
        failures-title="Some evaluations failed"
        idle-description="Run a playbook to see its evaluations and suggested changes."
        empty-description="No suggestions were returned for this document."
      >
        <template #actions>
          <gaia-button
            size="small"
            :disabled="!playbookStore.pendingSuggestions.length"
            :loading="applyingAllOf === 'evaluations'"
            @click="applyAllPending('evaluations')"
          >
            Apply all
          </gaia-button>
          <gaia-button size="small" quaternary @click="resetRun">Reset</gaia-button>
        </template>

        <gaia-collapse accordion>
          <n-collapse-item v-for="group in groupedSuggestions" :key="group.name" :name="group.name">
            <template #header>
              <gaia-text size="dense" weight="semibold" tone="ink">{{ group.name }}</gaia-text>
            </template>
            <template #header-extra>
              <gaia-count-pill>{{ group.cards.length }}</gaia-count-pill>
            </template>

            <n-flex vertical :size="10" :wrap="false">
              <result-card
                v-for="card in group.cards"
                :key="card.suggestion.id"
                :title="card.suggestion.title"
                :state="isActionableSuggestion(card.suggestion) ? card.suggestion.status : null"
                :failed="playbookStore.failedSuggestionIds.has(card.suggestion.id)"
                :focusable="canFocusSuggestion(card.suggestion)"
                @activate="focusSuggestion(card.suggestion)"
              >
                <template #header-extra>
                  <gaia-status-chip
                    v-if="playbookStore.failedSuggestionIds.has(card.suggestion.id)"
                    tone="warning"
                  >
                    Not applied
                  </gaia-status-chip>
                  <!-- Classifications with no suggestion action have no status to
                       report; the risk pill and class chip carry the result. -->
                  <gaia-status-chip
                    v-else-if="isActionableSuggestion(card.suggestion)"
                    :tone="suggestionChip(card.suggestion).tone"
                  >
                    {{ suggestionChip(card.suggestion).label }}
                  </gaia-status-chip>
                </template>

                <template #meta>
                  <template v-if="card.suggestion.risk_assessment">
                    <gaia-risk-pill
                      v-if="severityHasRisk(group.name)"
                      :level="severityRiskLevel(group.name)"
                      pill
                    >
                      {{ card.suggestion.risk_assessment.label }}
                    </gaia-risk-pill>
                    <gaia-tag v-else variant="muted" size="small" round>
                      {{ card.suggestion.risk_assessment.label }}
                    </gaia-tag>
                  </template>
                  <gaia-tag
                    v-if="card.suggestion.matched_option_names"
                    variant="muted"
                    size="small"
                    round
                  >
                    {{ card.suggestion.matched_option_names }}
                  </gaia-tag>
                  <suggestion-actions
                    :suggestion="card.suggestion"
                    :actionable="isActionableSuggestion(card.suggestion)"
                    :focusing-id="playbookStore.focusingSuggestionId"
                    :commenting-id="commentingSuggestionId"
                    :applying-id="playbookStore.applyingSuggestionId"
                    @focus="focusSuggestion"
                    @comment="addSuggestionComment"
                    @apply="applySuggestion"
                    @decline="playbookStore.declineSuggestion(card.suggestion.id)"
                  />
                </template>

                <preview-segments v-if="card.segments.length" :segments="card.segments" />

                <template #reasoning>
                  <p>{{ card.suggestion.explanation }}</p>
                  <p
                    v-if="
                      card.suggestion.result_type === 'extraction' &&
                      card.suggestion.extraction_highlighted_text?.length
                    "
                  >
                    <strong>Highlighted text:</strong>
                    {{ card.suggestion.extraction_highlighted_text.join(' | ') }}
                  </p>
                </template>
              </result-card>
            </n-flex>
          </n-collapse-item>
        </gaia-collapse>
      </result-pane>
    </div>

    <div v-show="activeTab === 'clause-validation'">
      <result-pane
        :status="playbookStore.clauseValidationStatus"
        :progress-label="playbookStore.clauseValidationProgressLabel"
        :failures="playbookStore.clauseValidationFailures"
        failures-title="Some clause validations failed"
        idle-description="Run a playbook to check this document's clauses against your positions."
        empty-description="No clause validations were returned for this document."
      >
        <template #actions>
          <gaia-button
            size="small"
            :disabled="!playbookStore.pendingClauseSuggestions.length"
            :loading="applyingAllOf === 'clauses'"
            @click="applyAllPending('clauses')"
          >
            Apply all
          </gaia-button>
          <gaia-button size="small" quaternary @click="resetRun">Reset</gaia-button>
        </template>

        <gaia-collapse accordion>
          <n-collapse-item v-for="group in groupedClauseEntries" :key="group.key" :name="group.key">
            <template #header>
              <gaia-text size="dense" weight="semibold" tone="ink">{{ group.name }}</gaia-text>
            </template>
            <template #header-extra>
              <gaia-count-pill>{{ group.entries.length }}</gaia-count-pill>
            </template>

            <n-flex vertical :size="10" :wrap="false">
              <template v-for="entry in group.entries" :key="entry.id">
                <result-card
                  v-for="card in entry.cards"
                  :key="card.suggestion.id"
                  :title="entry.evaluationName"
                  :state="card.suggestion.status"
                  :failed="playbookStore.failedSuggestionIds.has(card.suggestion.id)"
                  :focusable="canFocusSuggestion(card.suggestion)"
                  @activate="focusSuggestion(card.suggestion)"
                >
                  <template #header-extra>
                    <gaia-status-chip
                      v-if="playbookStore.failedSuggestionIds.has(card.suggestion.id)"
                      tone="warning"
                    >
                      Not applied
                    </gaia-status-chip>
                    <gaia-status-chip v-else :tone="suggestionChip(card.suggestion).tone">
                      {{ suggestionChip(card.suggestion).label }}
                    </gaia-status-chip>
                  </template>

                  <template #meta>
                    <gaia-risk-pill :level="clauseRiskLevel(entry.riskLevel)" pill>
                      {{ entry.optionName || group.name }}
                    </gaia-risk-pill>
                    <gaia-text v-if="entry.clause" size="micro" tone="subtle">
                      {{ entry.clause.name }}
                    </gaia-text>
                    <suggestion-actions
                      :suggestion="card.suggestion"
                      :actionable="true"
                      :focusing-id="playbookStore.focusingSuggestionId"
                      :commenting-id="commentingSuggestionId"
                      :applying-id="playbookStore.applyingSuggestionId"
                      @focus="focusSuggestion"
                      @comment="addSuggestionComment"
                      @apply="applySuggestion"
                      @decline="playbookStore.declineSuggestion(card.suggestion.id)"
                    />
                  </template>

                  <preview-segments :segments="card.segments" />

                  <template #reasoning>
                    <p v-if="entry.reasoning">{{ entry.reasoning }}</p>
                    <p v-if="card.suggestion.explanation">{{ card.suggestion.explanation }}</p>
                  </template>
                </result-card>

                <!-- A validated clause with no redline to apply: the option it matched
                     and the matched text are the whole result. -->
                <result-card v-if="!entry.cards.length" :title="entry.evaluationName">
                  <template #meta>
                    <gaia-risk-pill :level="clauseRiskLevel(entry.riskLevel)" pill>
                      {{ entry.optionName || group.name }}
                    </gaia-risk-pill>
                    <gaia-text v-if="entry.clause" size="micro" tone="subtle">
                      {{ entry.clause.name }}
                    </gaia-text>
                  </template>

                  <preview-segments
                    v-if="entry.highlightedText.length"
                    :segments="[{ kind: 'unchanged', text: entry.highlightedText.join(' | ') }]"
                  />

                  <template v-if="entry.reasoning" #reasoning>
                    <p>{{ entry.reasoning }}</p>
                  </template>
                </result-card>
              </template>
            </n-flex>
          </n-collapse-item>
        </gaia-collapse>
      </result-pane>
    </div>

    <div v-show="activeTab === 'uncommons'">
      <result-pane
        :status="playbookStore.uncommonsStatus"
        :progress-label="playbookStore.uncommonsProgressLabel"
        :failures="playbookStore.uncommonsFailures"
        failures-title="Some uncommons checks failed"
        idle-description="Run a playbook to surface passages that are unusual for this kind of contract."
        empty-description="No uncommon passages were found in this document."
      >
        <template #actions>
          <gaia-button size="small" quaternary @click="resetRun">Reset</gaia-button>
        </template>

        <gaia-collapse accordion>
          <n-collapse-item
            v-for="group in groupedUncommonsFindings"
            :key="group.bucket"
            :name="group.bucket"
          >
            <template #header>
              <gaia-text size="dense" weight="semibold" tone="ink">{{ group.bucket }}</gaia-text>
            </template>
            <template #header-extra>
              <gaia-count-pill>{{ group.findings.length }}</gaia-count-pill>
            </template>

            <n-flex vertical :size="10" :wrap="false">
              <result-card
                v-for="finding in group.findings"
                :key="finding.id"
                :title="`Finding ${finding.number}`"
                :state="finding.status === 'closed' ? 'closed' : null"
                :focusable="canFocusFinding(finding)"
                @activate="focusFinding(finding)"
              >
                <template #header-extra>
                  <gaia-status-chip :tone="finding.status === 'closed' ? 'success' : 'neutral'">
                    {{ finding.status === 'closed' ? 'Closed' : 'Open' }}
                  </gaia-status-chip>
                </template>

                <template #meta>
                  <gaia-risk-pill
                    v-if="severityHasRisk(group.bucket)"
                    :level="severityRiskLevel(group.bucket)"
                    pill
                  >
                    {{ group.bucket }}
                  </gaia-risk-pill>
                  <gaia-tag v-else variant="muted" size="small" round>{{ group.bucket }}</gaia-tag>
                  <n-flex
                    align="center"
                    :size="2"
                    :wrap="false"
                    :style="{ marginLeft: 'auto', flex: 'none' }"
                    @click.stop
                  >
                    <gaia-tooltip v-if="canFocusFinding(finding)">
                      <template #trigger>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          :loading="playbookStore.focusingFindingId === finding.id"
                          aria-label="Show passage in document"
                          @click.stop="focusFinding(finding)"
                        >
                          <n-icon :size="15" :component="IconSearch" />
                        </gaia-icon-button>
                      </template>
                      Show in document
                    </gaia-tooltip>
                    <gaia-tooltip>
                      <template #trigger>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          :disabled="!canFocusFinding(finding)"
                          :loading="commentingFindingId === finding.id"
                          aria-label="Add finding as Word comment"
                          @click.stop="addFindingComment(finding)"
                        >
                          <n-icon :size="15" :component="IconChatActive" />
                        </gaia-icon-button>
                      </template>
                      Add as comment
                    </gaia-tooltip>
                    <gaia-tooltip>
                      <template #trigger>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          :aria-label="
                            finding.status === 'open' ? 'Close finding' : 'Reopen finding'
                          "
                          @click.stop="
                            finding.status === 'open'
                              ? playbookStore.closeFinding(finding)
                              : playbookStore.revertFinding(finding)
                          "
                        >
                          <n-icon
                            :size="15"
                            :component="finding.status === 'open' ? IconClose : IconUndo"
                          />
                        </gaia-icon-button>
                      </template>
                      {{ finding.status === 'open' ? 'Close finding' : 'Reopen finding' }}
                    </gaia-tooltip>
                  </n-flex>
                </template>

                <gaia-text
                  tag="p"
                  size="dense"
                  tone="text"
                  :style="{ margin: '8px 0 0', lineHeight: 1.5, overflowWrap: 'anywhere' }"
                >
                  {{ finding.reason }}
                </gaia-text>
              </result-card>
            </n-flex>
          </n-collapse-item>
        </gaia-collapse>
      </result-pane>
    </div>
  </n-flex>
</template>

<style scoped>
/* The cascader is only there to own the menu: it fills the button's box so the
   menu anchors under the icon, while the button takes the clicks. */
.run-picker {
  position: relative;
  display: flex;
  flex: none;
}

.run-picker__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}
</style>

<style>
/* Cascader menu is teleported to body, so this override is intentionally unscoped
   and namespaced by the menu class. Let each column size to its content (up to the
   themed max height) instead of the default fixed height — with only a handful of
   playbooks the first column would otherwise be mostly empty space. */
.playbook-run-cascader-menu .n-cascader-submenu {
  height: auto;
  max-height: var(--n-menu-height);
}
</style>
